import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import { useSeo } from '@/hooks/useSeo';
import {
  filterMovies,
  getAllRegions,
  getAllYears,
  getMoviesByType,
  getTypeLabel,
  sortMovies,
} from '@/utils/movies';
import type { Movie } from '@/types/movie';

const PAGE_SIZE = 8;

const sortOptions = [
  { value: 'latest', label: '最新更新' },
  { value: 'hot', label: '最多播放' },
  { value: 'rating', label: '评分最高' },
];

const ratingOptions = [
  { value: '', label: '全部评分' },
  { value: '9', label: '9 分以上' },
  { value: '8', label: '8 分以上' },
  { value: '7', label: '7 分以上' },
];

interface CatalogViewProps {
  typeParam: string;
  mode?: 'pagination' | 'loadmore';
}

export default function CatalogView({ typeParam, mode = 'pagination' }: CatalogViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const type = typeParam === 'tv' ? 'tv' : typeParam === 'movie' ? 'movie' : 'all';
  const typeLabel = getTypeLabel(type);
  const base = useMemo(() => getMoviesByType(type), [type]);

  const genre = searchParams.get('genre') || '';
  const region = searchParams.get('region') || '';
  const year = searchParams.get('year') || '';
  const rating = searchParams.get('rating') || '';
  const sort = (searchParams.get('sort') as 'latest' | 'hot' | 'rating') || 'latest';
  const page = Number(searchParams.get('page') || '1');

  const genres = useMemo(() => Array.from(new Set(base.flatMap((movie) => movie.genres))), [base]);
  const regions = useMemo(() => getAllRegions(), []);
  const years = useMemo(() => getAllYears(), []);

  const results = useMemo<Movie[]>(() => {
    const filtered = filterMovies(base, {
      genre: genre || undefined,
      region: region || undefined,
      year: year || undefined,
      minRating: rating ? Number(rating) : undefined,
    });
    return sortMovies(filtered, sort);
  }, [base, genre, region, year, rating, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [genre, region, year, rating, sort]);

  const description =
    type === 'movie'
      ? '汇集全球及华语地区的高清电影资源，覆盖科幻、悬疑、动作、爱情等热门类型，支持多条件筛选，快速找到想看的好片。'
      : type === 'tv'
        ? '精选海量高清电视剧资源，国产、美剧、日韩剧一应俱全，支持按类型、地区与评分筛选，追剧追得更轻松。'
        : '影视资源综合频道，涵盖电影与电视剧多种类型，支持多条件筛选与排序，快速发现你想看的作品。';

  useSeo({
    title: `${typeLabel} - 香蕉视频app`,
    description,
    keywords: `${typeLabel},香蕉视频app,高清影视,${typeLabel}资源,在线观看,影视分类`,
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== 'page') {
      next.delete('page');
    }
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const prefix = `/category/${typeParam}`;
  const pagedResults =
    mode === 'pagination'
      ? results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
      : results.slice(0, visibleCount);

  const hasActiveFilter = Boolean(genre || region || year || rating);

  return (
    <div className="w-full">
      {/* header */}
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">{typeLabel}</span>
        </nav>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-50">
              {typeLabel}
              <span className="text-primary-400">{typeParam === 'movie' || typeParam === 'tv' ? '频道' : ''}</span>
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-400 max-w-2xl leading-relaxed">{description}</p>
          </div>
          <p className="text-sm text-foreground-500 shrink-0">
            共 <span className="text-primary-400 font-semibold">{results.length}</span> 部作品
          </p>
        </div>
      </section>

      {/* genre tabs */}
      <section className="w-full px-4 md:px-8 mt-6">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          <button
            type="button"
            onClick={() => updateParam('genre', '')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
              !genre
                ? 'bg-primary-500 text-background-50 font-semibold'
                : 'bg-background-100 text-foreground-200 border border-background-300 hover:text-primary-300'
            }`}
          >
            全部类型
          </button>
          {genres.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => updateParam('genre', item)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                genre === item
                  ? 'bg-primary-500 text-background-50 font-semibold'
                  : 'bg-background-100 text-foreground-200 border border-background-300 hover:text-primary-300'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </section>

      {/* filter bar */}
      <section className="w-full px-4 md:px-8 mt-4">
        <div className="rounded-lg border border-background-200 bg-background-100">
          <button
            type="button"
            onClick={() => setFiltersOpen((value) => !value)}
            className="md:hidden w-full flex items-center justify-between px-4 py-3 cursor-pointer"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-foreground-100">
              <i className="ri-filter-3-line text-primary-400"></i>
              筛选与排序
              {hasActiveFilter ? <span className="w-2 h-2 rounded-full bg-accent-500"></span> : null}
            </span>
            <i className={`${filtersOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg text-foreground-400`}></i>
          </button>

          <div className={`${filtersOpen ? 'block' : 'hidden'} md:block px-4 py-4 md:py-3`}>
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-foreground-500 shrink-0">地区</span>
                  <select
                    value={region}
                    onChange={(event) => updateParam('region', event.target.value)}
                    className="h-10 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-100 px-3 cursor-pointer outline-none focus:border-primary-400"
                  >
                    <option value="">全部地区</option>
                    {regions.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </label>

                <label className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-foreground-500 shrink-0">年份</span>
                  <select
                    value={year}
                    onChange={(event) => updateParam('year', event.target.value)}
                    className="h-10 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-100 px-3 cursor-pointer outline-none focus:border-primary-400"
                  >
                    <option value="">全部年份</option>
                    {years.map((item) => (
                      <option key={item} value={String(item)}>{item}</option>
                    ))}
                  </select>
                </label>

                <label className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-foreground-500 shrink-0">评分</span>
                  <select
                    value={rating}
                    onChange={(event) => updateParam('rating', event.target.value)}
                    className="h-10 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-100 px-3 cursor-pointer outline-none focus:border-primary-400"
                  >
                    {ratingOptions.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>

                {hasActiveFilter ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="h-10 px-3 rounded-md text-sm text-foreground-400 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-1"></i>重置
                  </button>
                ) : null}
              </div>

              <div className="md:ml-auto flex items-center gap-2 p-1 rounded-full bg-background-50 border border-background-300">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateParam('sort', option.value)}
                    className={`px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      sort === option.value
                        ? 'bg-primary-500 text-background-50 font-semibold'
                        : 'text-foreground-300 hover:text-primary-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* results */}
      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        {pagedResults.length > 0 ? (
          <div data-product-shop className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pagedResults.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center rounded-lg border border-background-200 bg-background-100">
            <i className="ri-movie-2-line text-4xl text-foreground-600"></i>
            <p className="mt-4 text-foreground-100 font-medium">没有找到符合条件的影视</p>
            <p className="mt-2 text-sm text-foreground-500">换个筛选条件试试看吧</p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>查看全部
            </button>
          </div>
        )}

        {mode === 'pagination' && results.length > PAGE_SIZE ? (
          <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => updateParam('page', String(currentPage - 1))}
              className="h-10 px-4 rounded-md border border-background-300 text-sm text-foreground-200 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              上一页
            </button>
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => updateParam('page', String(index + 1))}
                className={`w-10 h-10 rounded-md text-sm cursor-pointer transition-colors ${
                  currentPage === index + 1
                    ? 'bg-primary-500 text-background-50 font-semibold'
                    : 'border border-background-300 text-foreground-200 hover:border-primary-400 hover:text-primary-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => updateParam('page', String(currentPage + 1))}
              className="h-10 px-4 rounded-md border border-background-300 text-sm text-foreground-200 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              下一页
            </button>
          </div>
        ) : null}

        {mode === 'loadmore' && visibleCount < results.length ? (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              className="inline-flex items-center gap-2 h-11 px-8 rounded-md border border-background-300 text-sm font-medium text-foreground-100 hover:border-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              加载更多（剩余 {results.length - visibleCount} 部）
            </button>
          </div>
        ) : null}

        <div className="mt-8 text-center">
          <Link
            to={mode === 'pagination' ? `${prefix}/list` : prefix}
            className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer"
          >
            {mode === 'pagination' ? '前往深度筛选列表页' : '返回分类首页'}
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}